import { eventBus } from '../utils/EventEmitter.js';

/**
 * Base class for shader effects
 */
export class BaseEffect {
    /**
     * @param {string} name - Effect name
     * @param {ShaderManager} shaderManager - Shader manager instance
     * @param {Object} defaultParams - Default effect parameters
     */
    constructor(name, shaderManager, defaultParams = {}) {
        this.name = name;
        this.shaderManager = shaderManager;
        this.params = { ...defaultParams };
        this.uniforms = new Map();
        this.enabled = true;
        this.startTime = performance.now();
    }

    /**
     * Load shader source from file
     * @param {string} name - Shader name
     * @param {string} type - Shader type ('vert' or 'frag')
     * @returns {Promise<string>} Shader source
     */
    async loadShaderSource(name, type) {
        try {
            const response = await fetch(`/shaders/${name}.${type}`);
            if (!response.ok) {
                throw new Error(`Failed to load ${type} shader for ${name}`);
            }
            return await response.text();
        } catch (error) {
            console.error(`Error loading ${type} shader for ${name}:`, error);
            throw error;
        }
    }

    /**
     * Initialize the effect
     * @param {string} vertexSource - Vertex shader source
     * @param {string} fragmentSource - Fragment shader source
     */
    async init(vertexSource, fragmentSource) {
        try {
            // If sources are not provided, load from files
            if (!vertexSource) {
                vertexSource = await this.loadShaderSource(this.name, 'vert');
            }
            if (!fragmentSource) {
                fragmentSource = await this.loadShaderSource(this.name, 'frag');
            }

            await this.shaderManager.loadProgram(this.name, vertexSource, fragmentSource);
            this.setupUniforms();
            eventBus.emit('effect:initialized', { name: this.name });
        } catch (error) {
            console.error(`Failed to initialize ${this.name} effect:`, error);
            throw error;
        }
    }

    /**
     * Set up uniform definitions
     * This should be overridden by child classes
     */
    setupUniforms() {
        // Base uniforms that most effects will need
        this.addUniform('u_time', { type: '1f' });
        this.addUniform('u_resolution', { type: '2f' });
        this.addUniform('u_image', { type: '1i' });
    }

    /**
     * Add a uniform definition
     * @param {string} name - Uniform name
     * @param {Object} config - Uniform configuration
     */
    addUniform(name, config) {
        this.uniforms.set(name, config);
    }

    /**
     * Update effect parameters
     * @param {Object} params - New parameters
     */
    updateParams(params) {
        Object.assign(this.params, params);
        eventBus.emit('effect:updated', {
            name: this.name,
            params: this.params
        });
    }

    /**
     * Enable or disable the effect
     * @param {boolean} enabled - Whether to enable the effect
     */
    setEnabled(enabled) {
        this.enabled = enabled;
        eventBus.emit('effect:toggled', {
            name: this.name,
            enabled: this.enabled
        });
    }

    /**
     * Apply the effect
     * @param {number} time - Current time in seconds
     * @param {Object} resolution - Canvas resolution {width, height}
     */
    apply(time, resolution) {
        if (!this.enabled) return;

        const program = this.shaderManager.useProgram(this.name);
        
        // Update common uniforms
        const uniforms = {
            u_time: { value: time, type: '1f' },
            u_resolution: { value: [resolution.width, resolution.height], type: '2f' },
            u_image: { value: 0, type: '1i' }
        };

        // Add effect-specific uniforms
        this.updateUniforms(uniforms);

        // Set all uniforms
        this.shaderManager.setUniforms(uniforms);

        return program;  // Return the program for use in drawQuad
    }

    /**
     * Update effect-specific uniforms
     * This should be overridden by child classes
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        // Override in child classes
    }

    /**
     * Reset effect parameters to defaults
     */
    reset() {
        this.params = { ...this.defaultParams };
        eventBus.emit('effect:reset', { name: this.name });
    }

    /**
     * Get current parameters
     * @returns {Object} Current parameters
     */
    getParams() {
        return { ...this.params };
    }

    /**
     * Get uniform definitions
     * @returns {Map} Uniform definitions
     */
    getUniforms() {
        return new Map(this.uniforms);
    }

    /**
     * Check if effect is enabled
     * @returns {boolean} Whether effect is enabled
     */
    isEnabled() {
        return this.enabled;
    }

    /**
     * Get effect name
     * @returns {string} Effect name
     */
    getName() {
        return this.name;
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        const config = {};
        Object.entries(this.params).forEach(([param, value]) => {
            config[param] = {
                label: this.formatParamLabel(param),
                min: 0,
                max: typeof value === 'number' ? (value > 1 ? value * 2 : 1) : 1,
                step: typeof value === 'number' ? (value > 1 ? 1 : 0.01) : undefined
            };
        });
        return config;
    }

    /**
     * Format parameter name for UI
     * @param {string} param - Parameter name
     * @returns {string} Formatted label
     */
    formatParamLabel(param) {
        return param
            .replace(/([A-Z])/g, ' $1')
            .split(/[_\s]+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }
} 